import { useEffect, useRef, useState } from "react";
import { PALETTE, FONT_MONO } from "./tokens";

/* Day-in-the-life reel. Three scenes (morning / midday / evening),
   each types in a row, lands an "owner-approved ✓" stamp, then
   advances. Pure CSS / RAF, no extra deps. */

type Scene = {
  time: string;
  label: string; // chip label
  rows: { text: string; meta: string; ok?: boolean }[];
  footer: string;
};

const SCENES: Scene[] = [
  {
    time: "08:14",
    label: "Org pulse · this morning",
    rows: [
      { text: "Revenue today · $42,180 · ↑ 18% vs Tue avg", meta: "live", ok: true },
      { text: "Deal at risk · Acme · last reply 9d ago · Sarah owns", meta: "alert" },
      { text: "Sales-Eva drafted 7 follow-ups · awaiting Sarah", meta: "approve", ok: true },
    ],
    footer: "12 employees · 12 agents · all in sync",
  },
  {
    time: "13:02",
    label: "Team signal",
    rows: [
      { text: "Mike (sales) · 3 days no CRM activity", meta: "flag" },
      { text: "Beacon LLC · 4 unanswered emails across 2 reps", meta: "context", ok: true },
      { text: "Ops-Eva closed 14 tickets · shared notes to support", meta: "done", ok: true },
    ],
    footer: "shared context · 2 agents collaborating",
  },
  {
    time: "18:30",
    label: "End-of-day · across the company",
    rows: [
      { text: "Closed today · $61,420 across 4 reps", meta: "logged", ok: true },
      { text: "Falling behind · onboarding queue (12 → 18)", meta: "flag" },
      { text: "Owner decision · refund req · Beacon LLC", meta: "your call" },
    ],
    footer: "company-wide · 0 dropped balls",
  },
];

export const HeroConsole = () => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced.current) {
      setShown(SCENES[0].rows.length);
      return;
    }
    // Show first row immediately so the console is never blank on mount.
    setShown(1);
    let mounted = true;
    let rowTimer: number | undefined;
    let sceneTimer: number | undefined;
    const runScene = (idx: number) => {
      if (!mounted) return;
      setSceneIdx(idx);
      setShown(1);
      const rows = SCENES[idx].rows.length;
      let i = 1;
      rowTimer = window.setInterval(() => {
        i += 1;
        setShown(i);
        if (i >= rows) {
          if (rowTimer) clearInterval(rowTimer);
          sceneTimer = window.setTimeout(() => runScene((idx + 1) % SCENES.length), 2400);
        }
      }, 700);
    };
    runScene(0);
    return () => {
      mounted = false;
      if (rowTimer) clearInterval(rowTimer);
      if (sceneTimer) clearTimeout(sceneTimer);
    };
  }, []);

  const scene = SCENES[sceneIdx];

  return (
    <div aria-hidden style={{
      background: PALETTE.dark, color: "#fff",
      borderRadius: 18,
      border: `1px solid ${PALETTE.line}`,
      boxShadow: "0 30px 60px -30px rgba(0,0,0,0.5)",
      fontFamily: FONT_MONO, fontSize: 12.5,
      display: "flex", flexDirection: "column", gap: 10,
      width: "100%", maxWidth: 440, position: "relative",
    }} className="cb-hero-console-card">
      <style>{`
        .cb-hero-console-card { padding: 18px 22px 18px 18px; }
        @media (max-width: 480px) {
          .cb-hero-console-card { padding: 14px 14px !important; font-size: 12px !important; }
          .cb-hero-console-row { white-space: normal !important; overflow: visible !important; text-overflow: clip !important; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        }
      `}</style>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%", background: "#7CD4A8",
          boxShadow: "0 0 8px rgba(124,212,168,0.7)",
          animation: "cb-led 1.6s ease-in-out infinite",
        }} />
        <span style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>
          CoBrain · LIVE · COMPANY
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10.5, color: "rgba(255,255,255,0.4)" }}>{scene.time}</span>
      </div>

      {/* Scene label */}
      <div key={`label-${sceneIdx}`} style={{
        fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase",
        color: PALETTE.amberSoft, fontWeight: 700,
        animation: "cb-fade 320ms cubic-bezier(.2,.8,.2,1) both",
      }}>
        ▸ {scene.label}
      </div>

      {/* Rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 96 }}>
        {scene.rows.map((r, i) => {
          const visible = i < shown;
          const ok = !!r.ok;
          return (
            <div key={`${sceneIdx}-${i}`} style={{
              display: "flex", alignItems: "center", gap: 10,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(6px)",
              transition: "opacity 260ms ease, transform 260ms cubic-bezier(.2,.8,.2,1)",
            }}>
              <span style={{ color: PALETTE.amberSoft }}>►</span>
              <span className="cb-hero-console-row" style={{ flex: 1, minWidth: 0, color: "rgba(255,255,255,0.92)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {r.text}
                {visible && i === shown - 1 && <span style={{ display: "inline-block", width: 7, marginLeft: 2, background: PALETTE.amberSoft, animation: "cb-caret 0.9s steps(1) infinite" }}>&nbsp;</span>}
              </span>
              <span style={{
                fontSize: 10.5, padding: "3px 8px", borderRadius: 999,
                background: ok ? "rgba(196,130,58,0.18)" : "rgba(255,255,255,0.08)",
                color: ok ? PALETTE.amberSoft : "rgba(255,255,255,0.6)",
                border: `1px solid ${ok ? "rgba(196,130,58,0.3)" : "rgba(255,255,255,0.12)"}`,
                whiteSpace: "nowrap",
                opacity: visible ? 1 : 0,
                transition: "opacity 220ms 120ms ease",
              }}>{ok ? "✓ approved" : "⏳ " + r.meta}</span>
            </div>
          );
        })}
      </div>

      <div style={{ paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 10.5, color: "rgba(255,255,255,0.45)", display: "flex", justifyContent: "space-between" }}>
        <span>{scene.footer}</span>
        <span style={{ display: "inline-flex", gap: 6 }}>
          {SCENES.map((_, i) => (
            <span key={i} style={{
              width: 5, height: 5, borderRadius: "50%",
              background: i === sceneIdx ? PALETTE.amberSoft : "rgba(255,255,255,0.18)",
            }} />
          ))}
        </span>
      </div>
    </div>
  );
};